import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, } from '@mui/material';
import type { Partnership } from '../types';
import { useUpdatePartnershipAdTag } from '../api';
import { useEnqueueSnackbar } from 'hooks';
import { createCabinetMutationTracking, trackCabinetSubmitAttempt, } from 'lib/datadog';

interface EditPartnershipAdTagDialogProps {
  partnership: Partnership | null;
  isOpen: boolean;
  onClose: () => void;
}

const EditPartnershipAdTagDialog: FC<EditPartnershipAdTagDialogProps> = ({
  partnership,
  isOpen,
  onClose,
}) => {
  const { onShowAlert, onShowErrorAlert } = useEnqueueSnackbar();
  const [adTag, setAdTag] = useState('');
  const adTagInputRef = useRef<HTMLInputElement | null>(null);
  const partnershipId = partnership?.id ?? null;
  const initialAdTag = partnership?.tag ?? '';
  const hasChanges = adTag !== initialAdTag;

  useEffect(() => {
    setAdTag(initialAdTag);
  }, [initialAdTag]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        adTagInputRef.current?.focus();
      });
    }
  }, [isOpen]);

  const { mutate, isPending } = useUpdatePartnershipAdTag({
    config: createCabinetMutationTracking({
      platform: 'facebook',
      entity: 'partnership_ad_tag',
      mode: 'edit',
      onSuccess: () => {
        onShowAlert('Partnership ad tag updated successfully');
        onClose();
      },
      onError: (error) => {
        onShowErrorAlert(error.message || 'Failed to update partnership ad tag');
      },
    }),
  });

  const handleSave = () => {
    if (!partnershipId) {
      onShowErrorAlert('Partnership id is missing');
      return;
    }

    trackCabinetSubmitAttempt('facebook', 'partnership_ad_tag', 'edit');

    mutate({
      partnershipId,
      tag: adTag.trim(),
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onClose={() => {
        if (!isPending) {
          onClose();
        }
      }}
    >
      <DialogTitle sx={{pb: 1}}>Edit Partnership Ad Tag</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {partnership?.tag ?? '-'}
        </Typography>
        <TextField
          label="New value"
          value={adTag}
          onChange={(event) => {
            setAdTag(event.target.value);
          }}
          fullWidth
          inputRef={adTagInputRef}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isPending || !hasChanges}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPartnershipAdTagDialog;
